import React, { useState, useEffect } from 'react';

const DAYS_OF_WEEK = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export default function Binks({ api, site }) {
  const [vesselData, setVesselData] = useState([]);
  const [startOnsite, setStartOnsite] = useState(85);
  const [startODY, setStartODY] = useState(53);
  const [startAtPort, setStartAtPort] = useState(0);
  const [containerDensity, setContainerDensity] = useState(1240);
  const [volumeOverrides, setVolumeOverrides] = useState({});
  const [bbMode, setBbMode] = useState('lvp'); // 'lvp' = BB from LVP per-day; 'summary' = BB from Weekly Summary volume / 7
  const [sck8Redirects, setSck8Redirects] = useState({}); // weekStart -> number per week
  const [portOverrides, setPortOverrides] = useState({}); // vessel -> override date
  const [vesselHighlights, setVesselHighlights] = useState({}); // vessel -> 'red' | 'blue' | 'pink' | null
  const [burndownStart, setBurndownStart] = useState(() => { const n = new Date(); return `${n.getFullYear()}-${String(n.getMonth()+1).padStart(2,'0')}-${String(n.getDate()).padStart(2,'0')}`; });
  const [weekStart, setWeekStart] = useState('');
  const [lvpData, setLvpData] = useState({});
  const [arrivedContainers, setArrivedContainers] = useState(new Set());

  useEffect(() => {
    const now = new Date();
    const sunday = new Date(now);
    sunday.setDate(now.getDate() - now.getDay());
    setWeekStart(`${sunday.getFullYear()}-${String(sunday.getMonth()+1).padStart(2,'0')}-${String(sunday.getDate()).padStart(2,'0')}`);
  }, []);

  useEffect(() => {
    if (!weekStart) return;
    [0, 1, 2].forEach(w => {
      const d = new Date(weekStart + 'T12:00:00');
      d.setDate(d.getDate() + w * 7);
      const ws = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
      fetch(`${api}/api/fclm/cache?site=${site}&date=LVP_${ws}&shift_type=plan`)
        .then(r => r.json()).then(data => {
          if (data && data.shiftData) setLvpData(prev => ({ ...prev, [ws]: data.shiftData }));
        }).catch(() => {});
    });
  }, [api, site, weekStart]);

  useEffect(() => {
    fetch(`${api}/api/fclm/cache?site=${site}&date=BINKS_DATA&shift_type=current`)
      .then(r => r.json()).then(data => {
        if (data && data.vessels) {
          setVesselData(data.vessels);
          if (data.startOnsite !== undefined) setStartOnsite(data.startOnsite);
          if (data.startODY !== undefined) setStartODY(data.startODY);
          if (data.startAtPort !== undefined) setStartAtPort(data.startAtPort);
          if (data.containerDensity !== undefined) setContainerDensity(data.containerDensity);
          if (data.burndownStart) setBurndownStart(data.burndownStart);
          if (data.volumeOverrides) setVolumeOverrides(data.volumeOverrides);
          if (data.bbMode) setBbMode(data.bbMode);
          if (data.portOverrides) setPortOverrides(data.portOverrides);
          if (data.vesselHighlights) setVesselHighlights(data.vesselHighlights);
          if (data.sck8Redirects) setSck8Redirects(data.sck8Redirects);
        }
      }).catch(() => {});
  }, [api, site]);

  // Load arrived containers (check last 7 digits of container IDs)
  useEffect(() => {
    fetch(`${api}/api/fclm/cache?site=${site}&date=YMS_CHECKINS&shift_type=map`)
      .then(r => r.json()).then(data => {
        const map = data?.map || {};
        setArrivedContainers(new Set(Object.keys(map)));
      }).catch(() => {});
  }, [api, site]);

  const save = () => {
    const payload = { vessels: vesselData, startOnsite, startODY, startAtPort, containerDensity, burndownStart, volumeOverrides, bbMode, portOverrides, vesselHighlights, sck8Redirects };
    fetch(`${api}/api/fclm/cache`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ site, date: 'BINKS_DATA', shift_type: 'current', data: payload })
    }).then(r => r.json()).then(d => {
      alert(`Saved! (${Object.keys(portOverrides).length} port overrides)`);
    }).catch(e => alert('Save failed: ' + e.message));
  };

  const handleUpload = (e) => {
    const file = e.target.files[0]; if (!file) return;
    file.text().then(text => {
      const lines = text.trim().split('\n').slice(1);
      const vessels = lines.map(line => {
        const cols = line.split(',').map(c => c.trim().replace(/^"|"$/g, ''));
        return { containerId: cols[0], vessel: cols[1], ead: cols[2] };
      }).filter(v => v.containerId && v.vessel && v.ead);
      setVesselData(vessels);
    });
    e.target.value = '';
  };

  // Remove a vessel entirely from the schedule: drop all its containers from
  // vesselData (which drives vesselGroups/bigVessels) and clean up its per-vessel
  // overrides/highlights. Persists on the next 💾 Save, like the other edits.
  const removeVessel = (vessel) => {
    if (!window.confirm(`Remove vessel "${vessel}" from the schedule? (Save to persist.)`)) return;
    setVesselData(prev => prev.filter(v => v.vessel !== vessel));
    setPortOverrides(prev => { const n = { ...prev }; delete n[vessel]; return n; });
    setVesselHighlights(prev => { const n = { ...prev }; delete n[vessel]; return n; });
  };

  const normalizeDate = (d) => {
    if (!d) return '';
    if (d.includes('/')) {
      const parts = d.split('/');
      if (parts.length === 3) {
        const yr = parts[2].length === 2 ? '20' + parts[2] : parts[2];
        return `${yr}-${parts[0].padStart(2,'0')}-${parts[1].padStart(2,'0')}`;
      }
    }
    return d;
  };

  // Move Sat/Sun to next Monday
  const adjustWeekend = (dateStr) => {
    if (!dateStr) return dateStr;
    const dt = new Date(dateStr + 'T12:00:00');
    const day = dt.getDay();
    if (day === 0) { dt.setDate(dt.getDate() + 1); } // Sun -> Mon
    else if (day === 6) { dt.setDate(dt.getDate() + 2); } // Sat -> Mon
    return `${dt.getFullYear()}-${String(dt.getMonth()+1).padStart(2,'0')}-${String(dt.getDate()).padStart(2,'0')}`;
  };

  // Get effective EAD for a container (applies port override + weekend shift)
  const getEffectiveEad = (v) => {
    const override = portOverrides[v.vessel];
    const raw = override ? normalizeDate(override) : normalizeDate(v.ead);
    return adjustWeekend(raw);
  };

  // Check if container arrived (match by last 7 digits)
  const isArrived = (containerId) => {
    const suffix = containerId.slice(-7);
    return arrivedContainers.has(suffix) || arrivedContainers.has('V' + suffix) || arrivedContainers.has(containerId);
  };

  const getWeekDates = (weekOffset) => {
    if (!weekStart) return [];
    const dates = [];
    for (let d = 0; d < 7; d++) {
      const dt = new Date(weekStart + 'T12:00:00');
      dt.setDate(dt.getDate() + weekOffset * 7 + d);
      dates.push(`${dt.getFullYear()}-${String(dt.getMonth()+1).padStart(2,'0')}-${String(dt.getDate()).padStart(2,'0')}`);
    }
    return dates;
  };

  const currentWeekNum = (() => {
    if (!weekStart) return 33;
    const d = new Date(weekStart + 'T12:00:00');
    const yearStart = new Date(d.getFullYear(), 0, 1);
    return Math.ceil(((d - yearStart) / 86400000 + yearStart.getDay() + 1) / 7);
  })();

  const getBB = (dateStr) => {
    if (!dateStr) return 0;
    const dt = new Date(dateStr + 'T12:00:00');
    const day = dt.getDay();
    const dayNames = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];
    const sundayOfWeek = new Date(dt); sundayOfWeek.setDate(dt.getDate() - day);
    const ws = `${sundayOfWeek.getFullYear()}-${String(sundayOfWeek.getMonth()+1).padStart(2,'0')}-${String(sundayOfWeek.getDate()).padStart(2,'0')}`;
    const override = volumeOverrides[ws];
    if (bbMode === 'summary') {
      // BB runs off the Weekly Summary volume: manual override if set, else the
      // LVP-derived weekly total (the grey placeholder), spread evenly / 7.
      const weekly = (override !== undefined && override !== '' && override !== null)
        ? (parseInt(override) || 0)
        : lvpWeekTotal(ws);
      return weekly > 0 ? Math.round(weekly / 7) : 0;
    }
    // 'lvp' mode: per-day LVP cartons (day+night); fall back to override/7.
    const shiftData = lvpData[ws];
    if (shiftData) {
      return (shiftData[dayNames[day] + '_day']?.cartons || 0) + (shiftData[dayNames[day] + '_night']?.cartons || 0);
    }
    if (override) return Math.round(override / 7);
    return 0;
  };

  // LVP-derived weekly total for a week (sum of day+night cartons across the 7
  // days). This is the grey placeholder the Weekly Summary shows when there's
  // no manual override, and the basis for BB in 'summary' mode when un-overridden.
  const lvpWeekTotal = (ws) => {
    const shiftData = lvpData[ws];
    if (!shiftData) return 0;
    const dayNames = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];
    let t = 0;
    for (const dn of dayNames) t += (shiftData[dn + '_day']?.cartons || 0) + (shiftData[dn + '_night']?.cartons || 0);
    return Math.round(t);
  };

  const getArrivalsForDate = (dateStr) => vesselData.filter(v => getEffectiveEad(v) === dateStr);

  // SCK8 redirects: spread evenly across 7 days for the week
  const getSck8Daily = (dateStr) => {
    if (!dateStr) return 0;
    const dt = new Date(dateStr + 'T12:00:00');
    const day = dt.getDay();
    const sundayOfWeek = new Date(dt); sundayOfWeek.setDate(dt.getDate() - day);
    const ws = `${sundayOfWeek.getFullYear()}-${String(sundayOfWeek.getMonth()+1).padStart(2,'0')}-${String(sundayOfWeek.getDate()).padStart(2,'0')}`;
    const weekly = parseInt(sck8Redirects[ws]) || 0;
    return Math.round(weekly / 7);
  };

  const getTotalArrivalsForDate = (dateStr) => getArrivalsForDate(dateStr).length + getSck8Daily(dateStr);

  // Vessel grouping with consolidation logic
  const vesselGroups = {};
  vesselData.forEach(v => { if (!vesselGroups[v.vessel]) vesselGroups[v.vessel] = []; vesselGroups[v.vessel].push(v); });
  const bigVessels = Object.entries(vesselGroups).filter(([, arr]) => arr.length >= 10);

  // Consolidate: for vessels with 10+, if <5 on a secondary day, merge to the day with most
  const getConsolidatedVesselDates = (vessel, arr, dates) => {
    const countByDate = {};
    arr.forEach(v => {
      const ead = getEffectiveEad(v);
      if (dates.includes(ead)) countByDate[ead] = (countByDate[ead] || 0) + 1;
    });
    const entries = Object.entries(countByDate).sort((a,b) => b[1] - a[1]);
    if (entries.length <= 1) return countByDate;
    const primaryDate = entries[0][0];
    const consolidated = {};
    entries.forEach(([date, count]) => {
      if (count < 5) {
        consolidated[primaryDate] = (consolidated[primaryDate] || 0) + count;
      } else {
        consolidated[date] = (consolidated[date] || 0) + count;
      }
    });
    // Ensure primary has its own count
    if (!consolidated[primaryDate]) consolidated[primaryDate] = entries[0][1];
    else if (entries[0][1] >= 5 && !Object.keys(consolidated).includes(primaryDate)) {
      consolidated[primaryDate] = entries[0][1];
    }
    return consolidated;
  };

  if (!weekStart) return null;

  const thStyle = { padding: '3px 6px', border: '1px solid #ccc', fontSize: '0.65rem', textAlign: 'center', background: '#f5f5f5', fontWeight: 600 };
  const tdStyle = { padding: '3px 6px', border: '1px solid #ccc', fontSize: '0.65rem', textAlign: 'center' };

  return (
    <div>
      <div style={{ display: 'flex', gap: 12, marginBottom: 12, alignItems: 'center', flexWrap: 'wrap' }}>
        <h2 className="section-title" style={{ margin: 0 }}>Vessel Forecast — Container & Vessel Planning</h2>
        <label className="btn btn-secondary" style={{ fontSize: '0.7rem', padding: '4px 10px', cursor: 'pointer' }}>
          📤 Upload CSV<input type="file" accept=".csv" style={{ display: 'none' }} onChange={handleUpload} />
        </label>
        <button className="btn btn-secondary" onClick={() => {
          const csv = 'Container ID,Vessel Name,EAD\nCXDU2179597,MSC EXAMPLE,8/15/2026\n';
          const a = document.createElement('a'); a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv' })); a.download = 'binks_template.csv'; a.click();
        }} style={{ fontSize: '0.7rem', padding: '4px 10px' }}>📥 Template</button>
        <button className="btn btn-primary" onClick={save} style={{ fontSize: '0.7rem', padding: '4px 10px' }}>💾 Save</button>
        <span style={{ fontSize: '0.7rem', color: '#666', marginLeft: 8 }}>BB plan from:</span>
        <button className={`btn ${bbMode === 'lvp' ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setBbMode('lvp')} style={{ fontSize: '0.7rem', padding: '4px 10px' }}>Use LVP</button>
        <button className={`btn ${bbMode === 'summary' ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setBbMode('summary')} style={{ fontSize: '0.7rem', padding: '4px 10px' }}>Use Weekly Summary</button>
        <span style={{ fontSize: '0.7rem', color: '#666' }}>{vesselData.length} containers loaded</span>
      </div>

      <div style={{ display: 'flex', gap: 16, marginBottom: 12, fontSize: '0.7rem', flexWrap: 'wrap' }}>
        <label>Burndown Start: <input type="date" value={burndownStart} onChange={e => setBurndownStart(e.target.value)} style={{ fontSize: '0.7rem' }} /></label>
        <label>Starting Onsite: <input type="number" value={startOnsite} onChange={e => setStartOnsite(parseInt(e.target.value)||0)} style={{ width: 50, fontSize: '0.7rem' }} /></label>
        <label>Starting ODY: <input type="number" value={startODY} onChange={e => setStartODY(parseInt(e.target.value)||0)} style={{ width: 50, fontSize: '0.7rem' }} /></label>
        <label>Starting At Port: <input type="number" value={startAtPort} onChange={e => setStartAtPort(parseInt(e.target.value)||0)} style={{ width: 50, fontSize: '0.7rem' }} /></label>
        <label>Container Density: <input type="number" value={containerDensity} onChange={e => setContainerDensity(parseInt(e.target.value)||0)} style={{ width: 60, fontSize: '0.7rem' }} /></label>
      </div>

      <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
        {/* Left: 3-week schedule */}
        <div style={{ flex: '2 1 500px', overflowX: 'auto' }}>
          {[0, 1, 2].map(weekOffset => {
            const dates = getWeekDates(weekOffset);
            if (dates.length === 0) return null;
            const weekNum = currentWeekNum + weekOffset;
            const dailyBB = dates.map(d => getBB(d));
            const dailyConsumed = dailyBB.map(bb => containerDensity > 0 ? Math.round(bb / containerDensity) : 0);
            const dailyArrivals = dates.map(d => getTotalArrivalsForDate(d));

            let available = startOnsite + startODY + startAtPort;
            for (let w = 0; w < weekOffset; w++) {
              getWeekDates(w).forEach(d => {
                if (d < burndownStart) return;
                available -= containerDensity > 0 ? Math.round(getBB(d) / containerDensity) : 0;
                available += getTotalArrivalsForDate(d);
              });
            }

            const dailySODAvailable = [];
            const dailyEODAvailable = [];
            const dailyEODBacklog = [];
            dates.forEach((d, i) => {
              if (d < burndownStart) {
                dailySODAvailable.push(null); dailyEODAvailable.push(null);
                dailyEODBacklog.push(null);
                return;
              }
              const sod = available; // before today's consumption + new at-port arrivals
              available = available - dailyConsumed[i] + dailyArrivals[i];
              const eod = available; // after today's consumption + new at-port arrivals
              dailySODAvailable.push(sod);
              dailyEODAvailable.push(eod);
              dailyEODBacklog.push(dailyConsumed[i] > 0 ? eod / dailyConsumed[i] : 0);
            });

            // Big vessels for this week, sorted by EAD
            const weekBigVessels = bigVessels
              .filter(([, arr]) => arr.some(v => dates.includes(getEffectiveEad(v))))
              .sort((a, b) => {
                const eadA = getEffectiveEad(a[1][0]) || '';
                const eadB = getEffectiveEad(b[1][0]) || '';
                return eadA.localeCompare(eadB);
              });

            const miscByDate = dates.map(d => getArrivalsForDate(d).filter(v => (vesselGroups[v.vessel]?.length || 0) < 10).length);

            return (
              <div key={weekOffset} style={{ marginBottom: 16 }}>
                <table style={{ borderCollapse: 'collapse', fontSize: '0.65rem' }}>
                  <thead>
                    <tr>
                      <th style={{ ...thStyle, textAlign: 'left', minWidth: 120 }}>Week {weekNum}</th>
                      <th style={thStyle}>EAD</th>
                      {dates.map(d => <th key={d} style={thStyle}>{DAYS_OF_WEEK[new Date(d+'T12:00:00').getDay()]} {d.slice(5)}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    <tr><td style={{ ...tdStyle, fontWeight: 700 }}>BB</td><td style={tdStyle}></td>
                      {dailyBB.map((v, i) => <td key={i} style={tdStyle}>{v ? v.toLocaleString() : ''}</td>)}</tr>
                    <tr><td style={{ ...tdStyle, fontWeight: 600 }}>Containers Consumed</td><td style={tdStyle}></td>
                      {dailyConsumed.map((v, i) => <td key={i} style={tdStyle}>{v || ''}</td>)}</tr>
                    <tr><td style={{ ...tdStyle, fontWeight: 600 }}>Total Available (SOD)</td><td style={tdStyle}></td>
                      {dailySODAvailable.map((v, i) => <td key={i} style={{ ...tdStyle, fontWeight: 600, color: v !== null && v < 0 ? '#c62828' : '#333' }}>{v !== null ? v : ''}</td>)}</tr>
                    <tr><td style={{ ...tdStyle, fontWeight: 600 }}>Total Available (EOD)</td><td style={tdStyle}></td>
                      {dailyEODAvailable.map((v, i) => <td key={i} style={{ ...tdStyle, fontWeight: 600, color: v !== null && v < 0 ? '#c62828' : '#333' }}>{v !== null ? v : ''}</td>)}</tr>
                    <tr><td style={{ ...tdStyle, fontWeight: 600 }}>Days of Backlog (EOD)</td><td style={tdStyle}></td>
                      {dailyEODBacklog.map((v, i) => {
                        if (v === null) return <td key={i} style={tdStyle}></td>;
                        const bg = v < 1 ? '#ef5350' : v < 2.1 ? '#ffcdd2' : '#fff';
                        return <td key={i} style={{ ...tdStyle, background: bg, fontWeight: 600 }}>{v > 0 ? v.toFixed(1) : '—'}</td>;
                      })}</tr>
                    <tr><td style={{ ...tdStyle, fontWeight: 600 }}>New At Port</td><td style={tdStyle}></td>
                      {dailyArrivals.map((v, i) => <td key={i} style={tdStyle}>{v || ''}</td>)}</tr>
                    <tr><td style={{ ...tdStyle, color: '#666' }}>MISC</td><td style={tdStyle}></td>
                      {miscByDate.map((v, i) => <td key={i} style={{ ...tdStyle, color: '#666' }}>{v || ''}</td>)}</tr>
                    <tr><td style={{ ...tdStyle, color: "#1565c0", fontWeight: 600 }}>SCK8 Redirects</td><td style={tdStyle}></td>
                      {dates.map((d, i) => <td key={i} style={{ ...tdStyle, color: "#1565c0" }}>{getSck8Daily(d) || ""}</td>)}</tr>
                    {weekBigVessels.map(([vessel, arr]) => {
                      const consolidated = getConsolidatedVesselDates(vessel, arr, dates);
                      const ead = getEffectiveEad(arr[0]);
                      const eadDisplay = ead ? `${parseInt(ead.slice(5,7))}/${parseInt(ead.slice(8,10))}/${ead.slice(0,4)}` : '';
                      const allArrived = arr.every(v => isArrived(v.containerId));
                      return (
                        <tr key={vessel} style={{ background: vesselHighlights[vessel] === 'red' ? '#ffcdd2' : vesselHighlights[vessel] === 'blue' ? '#bbdefb' : vesselHighlights[vessel] === 'pink' ? '#f8bbd0' : allArrived ? '#e8f5e9' : '#fff' }}>
                          <td style={{ ...tdStyle, textAlign: 'left', fontWeight: 600, color: allArrived ? '#2e7d32' : '#333' }}>{vessel}</td>
                          <td style={{ ...tdStyle, fontSize: '0.6rem' }}>{eadDisplay}</td>
                          {dates.map(d => {
                            const count = consolidated[d] || 0;
                            return <td key={d} style={tdStyle}>{count || ''}</td>;
                          })}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            );
          })}
        </div>

        {/* Right: Summary + Vessel list */}
        <div style={{ flex: '1 1 300px' }}>
          <div className="card" style={{ marginBottom: 12 }}>
            <h4 style={{ margin: '0 0 8px', fontSize: '0.8rem' }}>Weekly Summary</h4>
            <table style={{ borderCollapse: 'collapse', fontSize: '0.7rem' }}>
              <thead><tr>
                <th style={{ ...thStyle, background: '#333', color: '#fff' }}>Week</th>
                <th style={{ ...thStyle, background: '#333', color: '#fff' }}>Volume</th>
                <th style={{ ...thStyle, background: '#333', color: '#fff' }}>Arriving</th>
                <th style={{ ...thStyle, background: '#333', color: '#fff' }}>SCK8</th>
                <th style={{ ...thStyle, background: '#333', color: '#fff' }}>Consumed</th>
                <th style={{ ...thStyle, background: '#333', color: '#fff' }}>EOW</th>
              </tr></thead>
              <tbody>
                {[0, 1, 2].map(w => {
                  const dates = getWeekDates(w);
                  const ws = dates[0] || '';
                  const volume = dates.reduce((s, d) => s + getBB(d), 0);
                  const arriving = dates.reduce((s, d) => s + getTotalArrivalsForDate(d), 0);
                  const consumed = dates.reduce((s, d) => s + (containerDensity > 0 ? Math.round(getBB(d) / containerDensity) : 0), 0);
                  return (
                    <tr key={w}>
                      <td style={tdStyle}>Week {currentWeekNum + w}</td>
                      <td style={{ ...tdStyle, padding: '2px' }}><input type="text" value={volumeOverrides[ws] !== undefined && volumeOverrides[ws] !== '' ? volumeOverrides[ws] : ''} placeholder={(lvpWeekTotal(ws) || volume) ? (lvpWeekTotal(ws) || volume).toLocaleString() : ''} onChange={e => setVolumeOverrides(prev => ({...prev, [ws]: e.target.value === '' ? '' : (parseInt(e.target.value)||0)}))} style={{ width: 70, border: '1px solid #ccc', borderRadius: 2, textAlign: 'center', fontSize: '0.7rem', color: '#000' }} /></td>
                      <td style={tdStyle}>{arriving}</td>
                      <td style={{ ...tdStyle, padding: '2px' }}><input type="text" value={sck8Redirects[ws] || ''} onChange={e => setSck8Redirects(prev => ({...prev, [ws]: e.target.value}))} style={{ width: 40, border: '1px solid #ccc', borderRadius: 2, textAlign: 'center', fontSize: '0.7rem' }} /></td>
                      <td style={tdStyle}>{consumed}</td>
                      <td style={tdStyle}>{arriving - consumed}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <div style={{ marginTop: 8, fontSize: '0.65rem', color: '#666' }}>
              Onsite: {startOnsite} | ODY: {startODY} | Port: {startAtPort} | Density: {containerDensity}
            </div>
          </div>

          <div className="card">
            <h4 style={{ margin: '0 0 8px', fontSize: '0.8rem' }}>Vessel Schedule ({bigVessels.length} vessels)</h4>
            <table style={{ borderCollapse: 'collapse', fontSize: '0.6rem', width: '100%' }}>
              <thead><tr>
                <th style={{ ...thStyle, background: '#333', color: '#fff', textAlign: 'left' }}>Vessel</th>
                <th style={{ ...thStyle, background: '#333', color: '#fff' }}>Count</th>
                <th style={{ ...thStyle, background: '#333', color: '#fff' }}>Arrived</th>
                <th style={{ ...thStyle, background: '#333', color: '#fff' }}>EAD</th>
                <th style={{ ...thStyle, background: '#333', color: '#fff' }}>Port Arrival</th>
                <th style={{ ...thStyle, background: '#333', color: '#fff' }}>Wk</th>
                <th style={{ ...thStyle, background: '#333', color: '#fff' }}>Flag</th>
                <th style={{ ...thStyle, background: '#333', color: '#fff' }}></th>
              </tr></thead>
              <tbody>
                {bigVessels.sort((a, b) => getEffectiveEad(a[1][0]).localeCompare(getEffectiveEad(b[1][0]))).map(([vessel, arr]) => {
                  const ead = getEffectiveEad(arr[0]);
                  const eadDate = ead ? new Date(ead + 'T12:00:00') : null;
                  const week = eadDate ? Math.ceil(((eadDate - new Date(weekStart + 'T12:00:00')) / (7 * 86400000))) + currentWeekNum : '';
                  const arrived = arr.filter(v => isArrived(v.containerId)).length;
                  return (
                    <tr key={vessel} style={{ background: vesselHighlights[vessel] === 'red' ? '#ffcdd2' : vesselHighlights[vessel] === 'blue' ? '#bbdefb' : vesselHighlights[vessel] === 'pink' ? '#f8bbd0' : arrived === arr.length ? '#e8f5e9' : '#fff' }}>
                      <td style={{ ...tdStyle, textAlign: 'left', fontWeight: 600 }}>{vessel}</td>
                      <td style={tdStyle}>{arr.length}</td>
                      <td style={{ ...tdStyle, color: arrived > 0 ? '#2e7d32' : '#333' }}>{arrived}</td>
                      <td style={{ ...tdStyle, fontSize: '0.55rem' }}>{eadDate ? `${eadDate.getMonth()+1}/${eadDate.getDate()}` : ''}</td>
                      <td style={{ ...tdStyle, padding: '1px' }}>
                        <input type="date" value={portOverrides[vessel] || ''} onChange={e => setPortOverrides(prev => ({...prev, [vessel]: e.target.value}))}
                          style={{ width: 90, fontSize: '0.55rem', border: '1px solid #ddd', borderRadius: 2 }} />
                      </td>
                      <td style={tdStyle}>{week}</td>
                      <td style={{ ...tdStyle, padding: '1px' }}>
                        <select value={vesselHighlights[vessel] || ''} onChange={e => setVesselHighlights(prev => ({...prev, [vessel]: e.target.value || null}))} style={{ fontSize: '0.55rem', border: '1px solid #ddd', borderRadius: 2, width: 55 }}>
                          <option value="">—</option>
                          <option value="red">🔴 Slide</option>
                          <option value="blue">🔵 Port</option>
                          <option value="pink">🫙 Mismatch</option>
                        </select>
                      </td>
                      <td style={{ ...tdStyle, padding: '1px' }}>
                        <button onClick={() => removeVessel(vessel)} title={`Remove ${vessel} from the schedule`}
                          style={{ border: 'none', background: 'transparent', color: '#c0392b', cursor: 'pointer', fontWeight: 700, fontSize: '0.8rem', lineHeight: 1 }}>✕</button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
